import { FormGroup } from '@angular/forms';
import {
  FormRule,
  FormRuleEffect,
  FormRuleFactory,
  RuleAction,
} from 'src/app/shared/forms';
import { SubmissionConstants } from '../../models/constants/submissions-constants';

export class CustomerInfoRules {
  constructor(
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants
  ) {}

  getCustomRules(subValues: FormGroup): FormRule[] {
    const status = subValues.get('status')?.value;
    return [
      ...this.getCustomerNameRules(subValues, status),
      ...this.getStreetAddressRules(subValues, status),
      ...this.getCityRules(subValues, status),
      ...this.getZipCodeRules(subValues),
    ];
  }

  private getCustomerNameRules(
    subValues: FormGroup,
    status: string
  ): FormRule[] {
    const applyToCustomerName = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'ownerFullName', value);
    const trimmedValue = (subValues.get('ownerFullName')?.value ?? '').trim();
    if (status === this.submissionsConst.statusReadyToSubmit || status === this.submissionsConst.statusSubmittedManually) {
      return [
        this.ruleFact.createRule(`${trimmedValue.length === 0}`, [
          applyToCustomerName(RuleAction.MAKE_INVALID),
        ]),
      ];
    } else {
      return [
        this.ruleFact.createRule(`${true}`, [
          applyToCustomerName(RuleAction.SET_ERROR_MESSAGE, ''),
          applyToCustomerName(RuleAction.MAKE_NOT_REQUIRED),
        ]),
      ];
    }
  }

  private getStreetAddressRules(
    subValues: FormGroup,
    status: string
  ): FormRule[] {
    const applyToStreetAddress = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'homeStreet', value);

    const trimmedValue = (subValues.get('homeStreet')?.value ?? '').trim();

    const baseRules = [
      this.ruleFact.createRule(`${trimmedValue.toLowerCase() === 'tbd'}`, [
        applyToStreetAddress(RuleAction.MAKE_INVALID),
        applyToStreetAddress(
          RuleAction.SET_ERROR_MESSAGE,
          this.submissionsConst.streetAddressWarning
        ),
      ]),
    ];

    if (status === this.submissionsConst.statusReadyToSubmit || status === this.submissionsConst.statusSubmittedManually) {
      return [
        ...baseRules,
        this.ruleFact.createRule(`${!trimmedValue.length}`, [
          applyToStreetAddress(RuleAction.MAKE_INVALID),
        ]),
      ];
    } else {
      return [
        ...baseRules,
        this.ruleFact.createRule(`${true}`, [
          applyToStreetAddress(RuleAction.SET_ERROR_MESSAGE, ''),
          applyToStreetAddress(RuleAction.MAKE_NOT_REQUIRED),
        ]),
      ];
    }
  }

  private getCityRules(subValues: FormGroup, status: string): FormRule[] {
    const applyToCity = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'homeCity', value);
    const trimmedValue = (subValues.get('homeCity')?.value ?? '').trim();

    if (status === this.submissionsConst.statusReadyToSubmit || status === this.submissionsConst.statusSubmittedManually) {
      return [
        this.ruleFact.createRule(`${trimmedValue.length === 0}`, [
          applyToCity(RuleAction.MAKE_INVALID),
        ]),
      ];
    } else {
      return [
        this.ruleFact.createRule(`${true}`, [
          applyToCity(RuleAction.SET_ERROR_MESSAGE, ''),
          applyToCity(RuleAction.MAKE_NOT_REQUIRED),
        ]),
      ];
    }
  }

  private getZipCodeRules(subValues: FormGroup): FormRule[] {
    const zipCode = subValues.get('homePostalCode')?.value;
    const isZipCodeValid = new RegExp(/^\d{5}(-\d{4})?$/).test(zipCode);
    return [
      this.ruleFact.createRule(`(${zipCode != null && !isZipCodeValid})`, [
        {
          action: RuleAction.MAKE_INVALID,
          affectingType: 'control',
          applyTo: 'homePostalCode',
        },
        {
          action: RuleAction.SET_ERROR_MESSAGE,
          affectingType: 'control',
          applyTo: 'homePostalCode',
          value: this.submissionsConst.zipCodeError,
        },
      ]),
    ];
  }

  private createBaseEffect(
    action: RuleAction,
    controlToApplyTo: string,
    value?: string
  ): FormRuleEffect {
    return {
      action: action,
      affectingType: 'control',
      applyTo: controlToApplyTo,
      value: value,
    };
  }
}
